import clsx from "clsx";
import { NavLink, useNavigate } from "react-router-dom";
import { Button } from "@headlessui/react";
import { Tooltip } from "react-tooltip";
import { FaUndo, FaEye, FaArrowLeft } from "react-icons/fa";
import { HiRefresh, HiShoppingCart } from "react-icons/hi";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/20/solid";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { ZoomLevel, ZOOM_OPTIONS } from "../utils/zoomUtils";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store";
import { resetMapControls } from "../store/mapSlice";
import { resetLayout, setOrientation, Orientation } from "../store/layoutSlice";
import { resetTraceStyle } from "../store/traceSlice";
import { resetProfileSettings } from "../store/profileSlice";
import { resetProductConfiguration } from "../store/productSlice";
import { clearPoints, initializePoints } from "../store/pointsSlice";
import { initializeLabels } from "../store/labelsSlice";
import { setZoomLevel, selectSelectedZoom } from "../store/zoomSlice";
import { useTranslation } from 'react-i18next';
import { setLanguage } from '../store/i18nSlice';
import Activity from "../types/Activity"; // Import Activity type
import { toggleSidebar } from "../store/sidebarSlice";

// Import flag images
import frFlag from '../assets/fr.png';
import enFlag from '../assets/en.png';
import esFlag from '../assets/es.png';
import { useEffect } from "react";

interface HeaderProps {
  isSidebarOpen: boolean;
}

const Header = ({
  isSidebarOpen,
}: HeaderProps) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLanguage = useSelector((state: RootState) => state.i18n.language);
  const cartItemCount = useSelector((state: RootState) => state.cart.items.length);
  const activities = useSelector((state: RootState) => state.activities.activities);
  const activeActivityIds = useSelector((state: RootState) => state.activities.activeActivityIds);
  const selectedZoom = useSelector(selectSelectedZoom);
  const currentOrientation = useSelector((state: RootState) => state.layout.orientation);

  useEffect(() => {
    if (currentLanguage !== i18n.language) {
      dispatch(setLanguage(i18n.language));
    }
  }, [i18n.language]);

  const handleRotate = () => {
      const nextOrientation: Orientation = currentOrientation === 'Portrait' ? 'Landscape' : 'Portrait';
      dispatch(setOrientation(nextOrientation));
  };

  const handleReset = () => {
      dispatch(resetMapControls());
      dispatch(resetLayout());
      dispatch(resetTraceStyle());
      dispatch(resetProfileSettings());
      dispatch(resetProductConfiguration());
      dispatch(initializeLabels());
      const activeActivitiesData = activities.filter((act: Activity) => activeActivityIds.includes(act.id));
      dispatch(clearPoints());
      dispatch(initializePoints(activeActivitiesData));
      dispatch(setZoomLevel('Fit to screen'));
      console.log("Réglages réinitialisés");
  };

  const handleZoomChange = (newZoom: ZoomLevel) => {
      dispatch(setZoomLevel(newZoom));
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    dispatch(setLanguage(lang));
  };

  const languageOptions = [
    { code: 'fr', label: 'Français', flag: frFlag },
    { code: 'en', label: 'English', flag: enFlag },
    { code: 'es', label: 'Español', flag: esFlag },
  ];

  // Helper to ensure currentLanguage is always a valid option
  const validLanguage = languageOptions.some(l => l.code === currentLanguage)
    ? currentLanguage
    : languageOptions[0].code;

  return (
    <header className="bg-[#222] w-full text-white border-b border-[#333] h-[61px] z-50 py-2 pl-2 pr-4 md:py-4 md:pl-4 md:pr-6 flex flex-row items-center justify-between flex-shrink-0">
      {/* Logo et bouton toggle */}
      <div className="flex items-center space-x-1 flex-shrink-0">
        <NavLink to={"/"} className="text-lg font-sans font-bold">
          <span className="inline sm:hidden text-xs">{t('header.logo')}</span>
          <span className="hidden sm:inline text-lg">{t('header.logo')}</span>
        </NavLink>
        <button
          onClick={() => dispatch(toggleSidebar())}
          className="flex items-center gap-1 px-3 py-1.5 rounded font-semibold shadow-md focus:outline-none text-white transition-colors ml-2"
          style={{ backgroundColor: '#FC4C02', boxShadow: '0 2px 8px 0 rgba(252,76,2,0.10)' }}
          onMouseOver={e => (e.currentTarget.style.backgroundColor = '#d63a00')}
          onMouseOut={e => (e.currentTarget.style.backgroundColor = '#FC4C02')}
        >
          {isSidebarOpen ? (
            <>
              <FaEye className="w-5 h-5" />
              <span className="text-xs font-normal">{t('sidebar.seePoster')}</span>
            </>
          ) : (
            <>
              <FaArrowLeft className="w-4 h-4" />
              <span className="text-xs font-normal">Configurateur</span>
            </>
          )}
        </button>
      </div>

      {/* Actions droites : tout le reste */}
      <div className="flex items-center space-x-2 md:space-x-4 flex-nowrap overflow-x-auto">
        {/* Zoom, rotation, reset : masqué sur mobile, visible à partir de md */}
        <div className="hidden md:flex flex-row items-center space-x-2 md:space-x-4">
          {/* Sélecteur de zoom */}
          <div className="w-32 md:w-52">
            <Listbox value={selectedZoom} onChange={handleZoomChange}>
              <ListboxButton
                className={clsx(
                  "relative block w-full rounded-lg bg-[#333] py-1 pr-8 pl-2 text-left text-sm/6 text-white min-w-[90px] md:min-w-[130px]",
                  "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
                )}
              >
                {selectedZoom === 'Fit to screen' ? t('header.zoomFit') : selectedZoom}
                <ChevronDownIcon
                  className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white/60"
                  aria-hidden="true"
                />
              </ListboxButton>
              <ListboxOptions
                anchor="bottom"
                transition
                className={clsx(
                  "w-[var(--button-width)] z-[100] mt-1 rounded-xl border border-white/5 bg-[#333] p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none",
                  "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
                )}
              >
                {ZOOM_OPTIONS.map((size) => (
                  <ListboxOption
                    key={size}
                    value={size}
                    className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-white/10"
                  >
                    <CheckIcon className="invisible size-4 fill-white group-data-[selected]:visible" />
                    <div className="text-sm/6 text-white">{size === 'Fit to screen' ? t('header.zoomFit') : size}</div>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </Listbox>
          </div>
          {/* Bouton rotation */}
          <Button
            onClick={handleRotate}
            data-tooltip-id="rotate-tooltip"
            data-tooltip-content={t('header.rotateTooltip')}
            className="cursor-pointer w-8 h-8 flex items-center justify-center bg-[#333] hover:opacity-75 text-white p-2 rounded"
          >
            <HiRefresh className="w-4 h-4" />
          </Button>
          {/* Bouton réinitialiser */}
          <Button
            onClick={handleReset}
            data-tooltip-id="reset-tooltip"
            data-tooltip-content={t('header.resetTooltip')}
            className="cursor-pointer w-8 h-8 flex items-center justify-center bg-[#333] hover:opacity-75 text-white p-2 rounded"
          >
            <FaUndo className="w-3 h-3" />
          </Button>
        </div>
        {/* Bouton panier */}
        <Button
          onClick={() => navigate('/cart')}
          data-tooltip-id="cart-tooltip"
          data-tooltip-content={t('header.cartTooltip')}
          className="relative rounded cursor-pointer bg-[#333333] text-white h-8 w-8 flex items-center justify-center hover:opacity-75"
        >
          <HiShoppingCart className="w-4 h-4" />
           {/* Badge panier */}
          {cartItemCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center leading-none z-10 border border-[#222]">
              {cartItemCount}
            </span>
          )}
        </Button>

        {/* Sélecteur de langue */}
        <div>
          <Listbox value={validLanguage} onChange={handleLanguageChange}>
            <ListboxButton
              className={clsx(
                "relative block rounded-lg bg-[#333] py-1 pr-6 pl-2 text-left text-sm/6 text-white min-w-[36px] md:min-w-[100px]",
                "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25"
              )}
              data-tooltip-id="language-tooltip"
            >
              <span className="inline md:hidden">
                <img src={languageOptions.find(l => l.code === validLanguage)?.flag} alt={validLanguage} className="w-5 h-5 inline" />
              </span>
              <span className="hidden md:inline">
                {languageOptions.find(l => l.code === validLanguage)?.label}
              </span>
            </ListboxButton>
            <ListboxOptions
              anchor="bottom"
              transition
              className={clsx(
                "w-[var(--button-width)] z-[100] mt-1 rounded-xl border border-white/5 bg-[#333] p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none",
                "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0"
              )}
            >
              {languageOptions.map((lang) => (
                <ListboxOption
                  key={lang.code}
                  value={lang.code}
                  className="group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-white/10"
                >
                  <img src={lang.flag} alt={lang.label} className="w-5 h-5 mr-2 rounded-sm" />
                  <div className="text-sm/6 text-white">{lang.code.toUpperCase()}</div>
                  <CheckIcon className="invisible w-5 h-5 ml-auto text-white group-data-[selected]:visible" />
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </div>
      </div>

      {/* Tooltips */}
      <Tooltip id="rotate-tooltip" place="bottom" style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }} opacity={1} />
      <Tooltip id="reset-tooltip" place="bottom" style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }} opacity={1} />
      <Tooltip id="cart-tooltip" place="bottom" style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }} opacity={1} />
      <Tooltip id="language-tooltip" place="bottom" style={{ backgroundColor: "#333", color: "#fff", zIndex: 9999 }} opacity={1} />
    </header>
  );
};

export default Header;